'use client';

/* ──────────────────────────────────────────────────────────────
   LOGIXA homepage — "industrial logistics control center".
   Implements landing/REDESIGN_SPEC.md sections 01–15 (09 social
   proof intentionally NOT shipped: no real customers yet).
   All copy comes from ../landing-v2/_content — no German in JSX.
   ────────────────────────────────────────────────────────────── */

import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { content, type Lang } from '../landing-v2/_content';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { readTheme, applyTheme, type PublicTheme } from '@/lib/publicTheme';
import './home.css';

const UI_LANG_KEY = 'logixa_lang';
const DEMO_HREF = '/kontakt';
const LOGIN_HREF = 'https://app.logixa.one/login';

/* ── icons (stroke-based, no emoji) ─────────────────────────── */
const S = (n: number) => ({
  width: n, height: n, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
});
const IcArrow = ({ n = 15 }: { n?: number }) => (<svg {...S(n)}><path d="M5 12h14M13 6l6 6-6 6" /></svg>);
const IcPlay = ({ n = 15 }: { n?: number }) => (<svg {...S(n)}><path d="M6 4l14 8-14 8z" /></svg>);
const IcCheck = ({ n = 15 }: { n?: number }) => (<svg {...S(n)}><path d="M4 12.5l5 5L20 6.5" /></svg>);
const IcPlus = ({ n = 15 }: { n?: number }) => (<svg {...S(n)}><path d="M12 5v14M5 12h14" /></svg>);
const IcShield = ({ n = 13 }: { n?: number }) => (<svg {...S(n)}><path d="M12 3l7 3v6c0 4.4-3 7.8-7 9-4-1.2-7-4.6-7-9V6z" /></svg>);
const IcInfo = ({ n = 14 }: { n?: number }) => (<svg {...S(n)}><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>);
const IcMenu = ({ n = 18 }: { n?: number }) => (<svg {...S(n)}><path d="M4 7h16M4 12h16M4 17h16" /></svg>);
const IcSun = ({ n = 15 }: { n?: number }) => (<svg {...S(n)}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>);
const IcMoon = ({ n = 15 }: { n?: number }) => (<svg {...S(n)}><path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" /></svg>);
const IcClose = ({ n = 18 }: { n?: number }) => (<svg {...S(n)}><path d="M6 6l12 12M18 6L6 18" /></svg>);

const STEP_ICONS = [
  (<svg key="1" {...S(15)}><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>),
  (<svg key="2" {...S(15)}><path d="M5 12h14M13 6l6 6-6 6" /></svg>),
  (<svg key="3" {...S(15)}><path d="M4 7v10a2 2 0 002 2h12a2 2 0 002-2V9H12L10 6H6a2 2 0 00-2 1z" /></svg>),
  (<svg key="4" {...S(15)}><path d="M16 20v-2a4 4 0 00-8 0v2M12 11a4 4 0 100-8 4 4 0 000 8z" /></svg>),
  (<svg key="5" {...S(15)}><path d="M12 3v10M8 7l4-4 4 4M5 15v4a2 2 0 002 2h10a2 2 0 002-2v-4" /></svg>),
];


const nf = (l: string) => new Intl.NumberFormat(l === 'de' ? 'de-DE' : l === 'en' ? 'en-GB' : 'de-DE');

export default function HomeV3({ forcedLang = 'de' }: { forcedLang?: Lang }) {
  const lang = forcedLang;
  const t = content[lang] ?? content.de;
  const router = useRouter();
  const [openFaq, setOpenFaq] = useState(0);
  const [menu, setMenu] = useState(false);
  const [basis, setBasis] = useState(false);

  // День/ночь — общий с PublicHeader источник (lib/publicTheme). Дефолт светлый;
  // без этого переключение на под-странице не доезжало до главной.
  const [theme, setTheme] = useState<PublicTheme>('light');
  useEffect(() => {
    const sync = () => setTheme(readTheme());
    sync();
    window.addEventListener('logixa-theme-change', sync);
    return () => window.removeEventListener('logixa-theme-change', sync);
  }, []);
  const toggleTheme = () => {
    const next: PublicTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    applyTheme(next);
  };

  // The URL is the language (server-rendered). A switch in the header navigates
  // to the matching locale URL instead of swapping content client-side, so SSR
  // and the first client render stay identical.
  useEffect(() => {
    try { localStorage.setItem(UI_LANG_KEY, forcedLang.toUpperCase()); } catch {}
    try { document.cookie = `${UI_LANG_KEY}=${forcedLang.toUpperCase()}; path=/; max-age=31536000; SameSite=Lax`; } catch {}
    const onChange = () => {
      let next = 'de';
      try { next = (localStorage.getItem(UI_LANG_KEY) || 'de').toLowerCase(); } catch {}
      const seg = (content as Record<string, unknown>)[next] ? next : 'de';
      if (seg !== forcedLang) router.push(seg === 'de' ? '/' : `/${seg}`);
    };
    window.addEventListener('logixa-lang-change', onChange);
    return () => window.removeEventListener('logixa-lang-change', onChange);
  }, [forcedLang, router]);

  /* ── ROI: every coefficient is documented in t.roi.basis ──── */
  const [fleet, setFleet] = useState(44);
  const [drivers, setDrivers] = useState(60);
  const [orders, setOrders] = useState(850);
  const [km, setKm] = useState(100000);
  const roi = useMemo(() => {
    const admin = orders * 12 * 7.5 + drivers * 12 * 15;   // 7,50 €/order + 15 €/driver·month
    const empty = fleet * km * 0.18 * 0.05 * 1.04;         // 18 % empty km, 5 % avoidable, 1,04 €/km
    const fuel = fleet * (km / 100) * 30 * 0.02;           // 30 l/100 km, 2 ct/l via live fuel prices
    const r = (n: number) => Math.round(n / 100) * 100;
    return { admin: r(admin), empty: r(empty), fuel: r(fuel), total: r(admin + empty + fuel) };
  }, [fleet, drivers, orders, km]);
  const fmt = nf(lang);
  const eur = (n: number) => `€ ${fmt.format(n)}`;

  const Logo = () => (<span className="lxh-logo">LOGI<i>X</i>A</span>);

  return (
    <div className="lxh">
      {/* ── 01 header ─────────────────────────────────────────── */}
      <header className="lxh-hd">
        <div className="lxh-c lxh-hd-in">
          <Link href="/" aria-label="LOGIXA"><Logo /></Link>
          <nav className="lxh-hd-nav">
            {t.nav.links.map((l) => <a key={l.label} href={l.href}>{l.label}</a>)}
          </nav>
          <div className="lxh-hd-act">
            <button className="lxh-theme" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Light' : 'Dark'}>
              {theme === 'dark' ? <IcSun /> : <IcMoon />}
            </button>
            <LanguageSwitcher compact />
            <a className="lxh-hd-login" href={LOGIN_HREF}>{t.nav.login}</a>
            <Link className="lxh-btn lxh-btn--pri" href={DEMO_HREF}>{t.nav.demo}</Link>
            <button className="lxh-burger" onClick={() => setMenu(!menu)} aria-label={t.nav.menu} aria-expanded={menu}>
              {menu ? <IcClose /> : <IcMenu />}
            </button>
          </div>
        </div>
        {menu && (
          <div className="lxh-c">
            <nav className="lxh-hd-mob" style={{ display: 'flex' }}>
              {t.nav.links.map((l) => <a key={l.label} href={l.href} onClick={() => setMenu(false)}>{l.label}</a>)}
              <a href={LOGIN_HREF}>{t.nav.login}</a>
              <Link className="lxh-btn lxh-btn--pri lxh-btn--sm" href={DEMO_HREF} onClick={() => setMenu(false)}>{t.nav.demo}</Link>
            </nav>
          </div>
        )}
      </header>

      {/* ── 02 hero ───────────────────────────────────────────── */}
      <section className="lxh-hero lxh-grid-bg">
        <div className="lxh-c lxh-hero-in">
          <div className="lxh-hero-l">
            <span className="lxh-kicker">{t.hero.kicker}</span>
            <h1 className="lxh-h1">{t.hero.h1}</h1>
            <p className="lxh-sub">{t.hero.sub}</p>
            <div className="lxh-hero-cta">
              <Link className="lxh-btn lxh-btn--pri" href={DEMO_HREF}>{t.hero.primary}<IcArrow /></Link>
              <a className="lxh-btn lxh-btn--gho" href="#plattform"><IcPlay n={13} />{t.hero.secondary}</a>
            </div>
            <div className="lxh-trust">{t.hero.trust.map((x) => <span key={x}>{x}</span>)}</div>
          </div>

          {/* Настоящий скриншот продукта (app.logixa.one, демо-организация
              LOGIXA Demo Logistics). Раньше тут была нарисованная вёрстка —
              она показывала интерфейс, которого в продукте нет. */}
          <div className="lxh-hero-r">
            <div className="lxh-mock-wrap">
              <img
                className="lxh-shot"
                src="/screenshots/app-orders.jpg"
                width={2720}
                height={1640}
                alt="LOGIXA order list: customer, pickup and delivery address, assigned driver with vehicle plate and delivery status"
              />
            </div>
            <div className="lxh-mock-note">{t.hero.demoNote}</div>
          </div>
        </div>
      </section>

      {/* ── 03 integrations ───────────────────────────────────── */}
      <section className="lxh-sec--strip lxh-soft" id="integrationen">
        <div className="lxh-c lxh-int">
          <div className="lxh-int-l">{t.integrations.label}</div>
          <div className="lxh-int-r">{t.integrations.items.map((i) => <span key={i}>{i}</span>)}</div>
        </div>
      </section>

      {/* ── 04 platform ───────────────────────────────────────── */}
      <section className="lxh-sec lxh-dark" id="plattform">
        <div className="lxh-c">
          <div className="lxh-head-c">
            <span className="lxh-kicker lxh-kicker--c">{t.platform.kicker}</span>
            <h2 className="lxh-h2 lxh-h2--big">{t.platform.h2}</h2>
            <p className="lxh-sub">{t.platform.sub}</p>
          </div>
          <div className="lxh-areas">
            {t.platform.areas.map((a) => (
              <article className="lxh-area" key={a.n}>
                <div className="lxh-area-n">{a.n}</div>
                <h3>{a.title}</h3>
                <p>{a.desc}</p>
                <div className="lxh-chips">{a.chips.map((c) => <span className="lxh-chip" key={c}>{c}</span>)}</div>
              </article>
            ))}
          </div>
          <div className="lxh-areas-cta">
            <Link className="lxh-link" href="/platform/funktionen">{t.platform.cta}<IcArrow n={14} /></Link>
          </div>
        </div>
      </section>

      {/* ── 05 dispatch ───────────────────────────────────────── */}
      <section className="lxh-sec" id="disposition">
        <div className="lxh-c lxh-split">
          <div className="lxh-split-l">
            <span className="lxh-kicker">{t.dispatch.kicker}</span>
            <h2 className="lxh-h2">{t.dispatch.h2}</h2>
            <p className="lxh-sub">{t.dispatch.sub}</p>
            <div className="lxh-checks">
              {t.dispatch.points.map((p) => (
                <div className="lxh-check" key={p.title}>
                  <IcCheck n={16} />
                  <div><b>{p.title}</b><span>{p.desc}</span></div>
                </div>
              ))}
            </div>
          </div>
          {/* Настоящий экран «Truck cost & profit planner» из продукта.
              Раньше здесь была нарисованная панель «Routenempfehlung» с бейджем
              LIVE и ценами на солярку по городам — такого экрана в продукте нет,
              а цены были вбиты руками под подписью «источники: Tankerkönig /
              E-Control / MITECO». Пересъёмка — scripts/capture-app-screenshots.js. */}
          <div className="lxh-route">
            <img
              className="lxh-shot lxh-shot--flat"
              src="/screenshots/app-route-planner.jpg"
              width={2720}
              height={1640}
              alt="LOGIXA trip calculation: distance, revenue, toll rate, consumption, diesel price and driver cost as inputs; toll, fuel, driver time, total cost, CO2 and profit per kilometre as the result"
            />
          </div>
        </div>
      </section>

      {/* ── 06 driver app ─────────────────────────────────────── */}
      <section className="lxh-sec lxh-dark">
        <div className="lxh-c lxh-split lxh-split--rev">
          <div className="lxh-split-l">
            <span className="lxh-kicker">{t.driver.kicker}</span>
            <h2 className="lxh-h2">{t.driver.h2}</h2>
            <p className="lxh-sub">{t.driver.sub}</p>
            <div className="lxh-flow">
              {t.driver.flow.map((f, i) => (
                <div key={f}>
                  <div className="lxh-flow-i"><span className="lxh-flow-n">{i + 1}</span><span>{f}</span></div>
                  {i < t.driver.flow.length - 1 && <div className="lxh-flow-l"><i /></div>}
                </div>
              ))}
            </div>
            <div className="lxh-chips">{t.driver.chips.map((c) => <span className="lxh-chip" key={c}>{c}</span>)}</div>
          </div>
          <div className="lxh-phones">
            {t.driver.phones.map((p) => (
              <article className="lxh-phone" key={p.tag}>
                <div className="lxh-phone-tag">{p.tag}</div>
                <h4>{p.title}</h4>
                {p.rows.map((r) => (<div className="lxh-phone-r" key={r.lbl}><span>{r.lbl}</span><b>{r.val}</b></div>))}
                <div className="lxh-phone-act">{p.action}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 07 compliance ─────────────────────────────────────── */}
      <section className="lxh-sec lxh-soft">
        <div className="lxh-c lxh-split lxh-split--dash">
          <div className="lxh-dash">
            <div className="lxh-dash-h">
              <div><span>{t.compliance.dashDriverLbl}</span> <b>{t.compliance.dashDriver}</b></div>
              <span className="lxh-badge-ok">{t.compliance.badge}</span>
            </div>
            <div className="lxh-bars">
              {t.compliance.bars.map((b) => (
                <div key={b.lbl}>
                  <div className="lxh-bar-h"><span>{b.lbl}</span><b>{b.val}</b></div>
                  <div className="lxh-bar"><i className={b.tone === 'warn' ? 'warn' : ''} style={{ width: `${b.pct}%` }} /></div>
                </div>
              ))}
            </div>
            <div className="lxh-docs">
              <h4>{t.compliance.docsTitle}</h4>
              {t.compliance.docs.map((d) => (
                <div className="lxh-doc" key={d.lbl}><span>{d.lbl}</span><b className={d.warn ? 'warn' : ''}>{d.val}</b></div>
              ))}
              <div className="lxh-alerts">{t.compliance.alerts}</div>
            </div>
          </div>
          <div className="lxh-split-l">
            <span className="lxh-kicker">{t.compliance.kicker}</span>
            <h2 className="lxh-h2">{t.compliance.h2}</h2>
            <p className="lxh-sub">{t.compliance.sub}</p>
            <div className="lxh-notes">
              {t.compliance.points.map((p) => (
                <div className="lxh-note" key={p.title}><b>{p.title}</b><span>{p.desc}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 08 onboarding ─────────────────────────────────────── */}
      <section className="lxh-sec">
        <div className="lxh-c">
          <div className="lxh-onb-top">
            <div className="lxh-onb-l">
              <span className="lxh-kicker">{t.onboarding.kicker}</span>
              <h2 className="lxh-h2">{t.onboarding.h2}</h2>
              <p className="lxh-sub">{t.onboarding.sub}</p>
            </div>
            <div className="lxh-onb-promo">
              <b>{t.onboarding.promoTitle}</b>
              <span>{t.onboarding.promoSub}</span>
              <Link className="lxh-btn lxh-btn--pri lxh-btn--sm" href={DEMO_HREF}>{t.onboarding.promoCta}<IcArrow n={14} /></Link>
            </div>
          </div>
          <div className="lxh-steps">
            {t.onboarding.steps.map((s, i) => (
              <article className="lxh-step" key={s.tag}>
                <div className="lxh-step-ic">{STEP_ICONS[i]}</div>
                <div className="lxh-step-tag">{s.tag}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 10 ROI ────────────────────────────────────────────── */}
      <section className="lxh-sec lxh-dark" id="roi">
        <div className="lxh-c lxh-roi">
          <div className="lxh-roi-l">
            <span className="lxh-kicker">{t.roi.kicker}</span>
            <h2 className="lxh-h2 lxh-h2--big">{t.roi.h2}</h2>
            <div className="lxh-sliders">
              <div className="lxh-sld">
                <label><span>{t.roi.fields[0].lbl}</span><b>{fleet}</b></label>
                <input type="range" min={3} max={500} value={fleet} onChange={(e) => setFleet(+e.target.value)} aria-label={t.roi.fields[0].lbl} />
              </div>
              <div className="lxh-sld">
                <label><span>{t.roi.fields[1].lbl}</span><b>{drivers}</b></label>
                <input type="range" min={3} max={800} value={drivers} onChange={(e) => setDrivers(+e.target.value)} aria-label={t.roi.fields[1].lbl} />
              </div>
              <div className="lxh-sld">
                <label><span>{t.roi.fields[2].lbl}</span><b>{fmt.format(orders)}</b></label>
                <input type="range" min={20} max={20000} step={10} value={orders} onChange={(e) => setOrders(+e.target.value)} aria-label={t.roi.fields[2].lbl} />
              </div>
              <div className="lxh-sld">
                <label><span>{t.roi.fields[3].lbl}</span><b>{fmt.format(km)} {t.roi.fields[3].unit}</b></label>
                <input type="range" min={20000} max={200000} step={5000} value={km} onChange={(e) => setKm(+e.target.value)} aria-label={t.roi.fields[3].lbl} />
              </div>
            </div>
          </div>
          <div className="lxh-roi-p">
            <h3>{t.roi.panelTitle}</h3>
            <div style={{ marginTop: 14 }}>
              <div className="lxh-roi-row"><span>{t.roi.lines[0]}</span><b>{eur(roi.admin)}</b></div>
              <div className="lxh-roi-row"><span>{t.roi.lines[1]}</span><b>{eur(roi.empty)}</b></div>
              <div className="lxh-roi-row"><span>{t.roi.lines[2]}</span><b>{eur(roi.fuel)}</b></div>
            </div>
            <div className="lxh-roi-total"><span>{t.roi.totalLbl}</span><b>{eur(roi.total)}</b></div>
            <div className="lxh-roi-basis">
              <button type="button" onClick={() => setBasis(!basis)} aria-expanded={basis}>
                <IcInfo />{basis ? t.roi.basisClose : t.roi.basisLink}
              </button>
              {basis && <ul>{t.roi.basis.map((b, i) => <li key={i}>{b}</li>)}</ul>}
            </div>
          </div>
        </div>
      </section>

      {/* ── 11 pricing ────────────────────────────────────────── */}
      <section className="lxh-sec" id="preise">
        <div className="lxh-c">
          <div className="lxh-price-head">
            <div className="lxh-price-head-l">
              <span className="lxh-kicker">{t.pricing.kicker}</span>
              <h2 className="lxh-h2">{t.pricing.h2}</h2>
              <p className="lxh-sub">{t.pricing.sub}</p>
            </div>
            <Link className="lxh-link" href="/pricing">{t.pricing.allLink}<IcArrow n={14} /></Link>
          </div>
          <div className="lxh-plans">
            {t.pricing.plans.map((p, i) => (
              <article className={`lxh-plan${i === 1 ? ' lxh-plan--hi' : ''}`} key={p.name}>
                {i === 1 && <span className="lxh-plan-badge">{t.pricing.badge}</span>}
                <div className="lxh-plan-n">{p.name}</div>
                <div className="lxh-plan-p"><b>{p.price}</b>{!p.custom && <span>{t.pricing.perMonth}</span>}</div>
                <div className="lxh-plan-scope">{p.scope}</div>
                <ul>{p.features.map((f) => <li key={f}><IcCheck n={14} />{f}</li>)}</ul>
                <Link className={`lxh-btn lxh-btn--sm ${i === 1 ? 'lxh-btn--pri' : 'lxh-btn--gho'}`} href={DEMO_HREF}>{t.pricing.cta}</Link>
              </article>
            ))}
          </div>
          <p className="lxh-price-note">
            {t.pricing.note[0]}<b>{t.pricing.note[1]}</b>{t.pricing.note[2]}<b>{t.pricing.note[3]}</b>{t.pricing.note[4]}
          </p>
        </div>
      </section>

      {/* ── 12 security ───────────────────────────────────────── */}
      <section className="lxh-sec--strip lxh-soft">
        <div className="lxh-c lxh-secu">
          <div className="lxh-secu-l">{t.security.title}</div>
          <div className="lxh-secu-r">
            {t.security.items.map((s) => (<span key={s}><IcShield />{s}</span>))}
          </div>
        </div>
      </section>

      {/* ── 13 FAQ ────────────────────────────────────────────── */}
      <section className="lxh-sec">
        <div className="lxh-c lxh-faq">
          <div className="lxh-faq-l">
            <span className="lxh-kicker">{t.faq.kicker}</span>
            <h2 className="lxh-h2">{t.faq.h2}</h2>
            <p className="lxh-sub">{t.faq.sub}</p>
          </div>
          <div>
            {t.faq.list.map((f, i) => (
              <div className={`lxh-q${openFaq === i ? ' open' : ''}`} key={f.q}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} aria-expanded={openFaq === i}>
                  {f.q}<span className="lxh-q-ic"><IcPlus n={16} /></span>
                </button>
                <div className="lxh-q-a">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 14 final CTA ──────────────────────────────────────── */}
      <section className="lxh-sec lxh-dark lxh-grid-bg">
        <div className="lxh-c lxh-final">
          <h2 className="lxh-h2 lxh-h2--big">{t.finalCta.h2}</h2>
          <p className="lxh-sub">{t.finalCta.sub}</p>
          <div className="lxh-final-cta">
            <Link className="lxh-btn lxh-btn--pri" href={DEMO_HREF}>{t.finalCta.primary}<IcArrow /></Link>
            <a className="lxh-btn lxh-btn--gho" href="#plattform"><IcPlay n={13} />{t.finalCta.secondary}</a>
          </div>
        </div>
      </section>

      {/* ── 15 footer ─────────────────────────────────────────── */}
      <footer className="lxh-ft">
        <div className="lxh-c">
          <div className="lxh-ft-top">
            <div className="lxh-ft-l">
              <Logo />
              <p>{t.footer.tagline}</p>
            </div>
            <div className="lxh-ft-cols">
              {t.footer.cols.map((c) => (
                <div key={c.title}>
                  <h4>{c.title}</h4>
                  <ul>{c.links.map((l) => <li key={l.label}><Link href={l.href}>{l.label}</Link></li>)}</ul>
                </div>
              ))}
            </div>
          </div>
          <div className="lxh-ft-bot">
            <span>{t.footer.copyright}</span>
            <div className="lxh-ft-legal">{t.footer.legal.map((l) => <Link key={l.label} href={l.href}>{l.label}</Link>)}</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
